import { Component, inject, Input, OnChanges } from '@angular/core';
import {
  CartRowFeatureOptionSelection,
  ProductConfigurationFeature,
  ProductConfigurationOption
} from '../../../models/product-configuration-types';
import { ProductConfigurationCartRowService } from '../../../services/cpq/product-configuration-cart-row.service';
import { CheckoutRow } from '../../../models/checkout';
import { CartRow } from '../../../state/cart/types';
import { CpqConfigService } from '../../../services/cpq/cpq-config.service';

@Component({
  selector: 'lib-cpq-cart-row-summary',
  templateUrl: './cpq-cart-row-summary.component.html',
  styleUrls: ['./cpq-cart-row-summary.component.css'],
  standalone: false
})
export class CpqCartRowSummaryComponent implements OnChanges {
  @Input()
  row: CartRow | CheckoutRow;

  @Input()
  displayMode: 'popover' | 'inline' | undefined;

  protected cpqConfigService = inject(CpqConfigService);

  selectionsGroupedByFeature: Map<string, ProductConfigurationOption[]>;
  keyedFeatures: Map<string, ProductConfigurationFeature>;

  constructor(protected productConfigurationCartRowService: ProductConfigurationCartRowService) {}

  ngOnChanges(): void {
    if (!this.row || !this.productConfigurationCartRowService.isProductConfiguration(this.row)) {
      return;
    }
    this.selectionsGroupedByFeature = this.getSelectionGroupedByFeature(this.row);
    this.keyedFeatures = this.keyFeatures(this.row);
  }

  getDisplayMode(): 'popover' | 'inline' {
    return this.displayMode || this.cpqConfigService.getCartRowSummaryMode();
  }

  getOptionNames(options: ProductConfigurationOption[], featureId?: string): string {
    return options.map(option => this.getOptionName(option, featureId)).join(', ');
  }

  getOptionName(option: ProductConfigurationOption, featureId?: string): string {
    const feature = featureId ? this.keyedFeatures?.get(featureId) : undefined;
    return feature ? this.cpqConfigService.getOptionLabel(feature, option, new Map()) : option.name;
  }

  getEditLink(): { path: string[]; queryParams: { config: string } } | undefined {
    const route = this.cpqConfigService.getEditConfigurationRoute();
    const productId = (this.row as CartRow).productId || (this.row as CheckoutRow).id;
    const partialId = (this.row as CartRow).productPartialId || (this.row as CheckoutRow).partialId;
    if (!route || !productId || !partialId) {
      return undefined;
    }
    return { path: [route, productId], queryParams: { config: partialId } };
  }

  private getSelectionGroupedByFeature(row: CartRow | CheckoutRow): Map<string, ProductConfigurationOption[]> {
    const result = new Map<string, ProductConfigurationOption[]>();
    row.extra.productConfigurationSelectedOptions.forEach(selection => {
      if (!result.has(selection.feature.id)) {
        result.set(selection.feature.id, []);
      }
      result.get(selection.feature.id).push(selection.option);
    });

    return result;
  }

  private keyFeatures(row: CartRow | CheckoutRow): Map<string, ProductConfigurationFeature> {
    const result = new Map<string, ProductConfigurationFeature>();
    row.extra.productConfigurationSelectedOptions.forEach((selection: CartRowFeatureOptionSelection) => {
      result.set(selection.feature.id, selection.feature);
    });
    return result;
  }
}
